import { useEffect, useCallback, useRef, useState } from 'react';
import { useSetAtom, useAtomValue, atom, useAtom } from 'jotai';
import { Notification, Novu } from '@novu/js';
import {
  LIST_SUPPLY_CHAIN_PROCESSES_URL,
  MATCH_LIST,
  NOVU_BACKEND_URL,
  NOVU_SOCKET_URL,
} from '@constants';
import axios from 'axios';
import { NotificationType } from '@components/Notification/Notification';
import { retryDelayForAttempt } from './retryBackoff';
import { parseRelatedEntities } from './notificationPayload';
import { unwrapNovuResult } from './novuResult';
import { refreshOnlyTheMatchOnScreen } from './matchOnScreen';
import {
  inboundProcessesAtom,
  matchAtom,
  notificationsAtom,
  outboundProcessesAtom,
  showNotificationAtom,
  stockRefreshAtom,
  unreadNotificationsCountAtom,
} from '../store/jotai';

const novuInstanceAtom = atom<Novu | null>(null);

interface UseNovuNotificationsOptions {
  applicationIdentifier?: string;
  subscriberId: string;
  subscriberHash?: string;
  backendUrl?: string;
  socketUrl?: string;
  isInitialisingNovu?: boolean;
}

export const useNovuNotifications = ({
  applicationIdentifier,
  subscriberId,
  subscriberHash,
  backendUrl = NOVU_BACKEND_URL,
  socketUrl = NOVU_SOCKET_URL,
  isInitialisingNovu: enabled = true,
}: UseNovuNotificationsOptions) => {
  const setNotifications = useSetAtom(notificationsAtom);
  const setShowNotification = useSetAtom(showNotificationAtom);
  const notifications = useAtomValue(notificationsAtom);
  const setInboundProcesses = useSetAtom(inboundProcessesAtom);
  const setOutboundProcesses = useSetAtom(outboundProcessesAtom);
  const setMatch = useSetAtom(matchAtom);
  const setUnreadNotificationsCount = useSetAtom(unreadNotificationsCountAtom);
  const setStockRefresh = useSetAtom(stockRefreshAtom);

  const [novuInstance, setNovuInstance] = useAtom(novuInstanceAtom);
  const [isInitialized, setIsInitialized] = useState(false);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const retryAttemptsRef = useRef(0);
  const connectedInstanceRef = useRef<Novu | null>(null);

  const mapNovuNotificationToInternal = useCallback(
    (novuNotification: any): NotificationType => {
      const payload = novuNotification.data || novuNotification.payload || {};

      return {
        id: novuNotification.id || novuNotification._id,
        notificationType: payload.notificationType || 'Unknown',
        title: payload.subject || novuNotification.subject || '',
        message: payload.body || novuNotification.body || '',
        createdAt: novuNotification.createdAt,
        readAt: novuNotification.read ? novuNotification.readAt : null,
        archivedAt: novuNotification.archived
          ? novuNotification.archivedAt
          : null,
        relatedEntities: parseRelatedEntities(payload.relatedEntities),
        isSilent: payload.isSilent || false,
      };
    },
    []
  );

  useEffect(() => {
    if (!enabled || !subscriberId || !applicationIdentifier) {
      return undefined;
    }

    let isEffectActive = true;

    const clearPendingRetry = () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };

    const discardCurrentInstance = () => {
      const instance = connectedInstanceRef.current;
      connectedInstanceRef.current = null;

      if (!instance?.socket) return;

      try {
        instance.socket.disconnect();
      } catch (error) {
        console.error('Failed to disconnect the Novu socket:', error);
      }
    };

    const refreshUnreadCount = async (novu: Novu) => {
      const countResult = await novu.notifications.count({
        read: false,
        archived: false,
      });

      const countData = unwrapNovuResult(countResult) as any;
      setUnreadNotificationsCount(countData?.count ?? countData?.total ?? 0);
    };

    const loadInbox = async (novu: Novu) => {
      const listResult = await novu.notifications.list({
        limit: 50,
        offset: 0,
        archived: false,
      });

      const listResponse = unwrapNovuResult(listResult) as any;
      setNotifications(
        (listResponse?.notifications || []).map(mapNovuNotificationToInternal)
      );
    };

    const connectToNovu = async () => {
      if (!isEffectActive) return;

      clearPendingRetry();
      discardCurrentInstance();

      try {
        const novu = new Novu({
          applicationIdentifier,
          subscriberId,
          subscriberHash,
          backendUrl,
          socketUrl,
        });

        connectedInstanceRef.current = novu;

        novu.on('socket.connect.resolved', ({ error }) => {
          if (error) {
            console.error('Novu socket connection failed:', error);
            return;
          }
          retryAttemptsRef.current = 0;
        });

        novu.on('notifications.notification_received', (event) => {
          const notification = mapNovuNotificationToInternal(event.result);

          setNotifications((prev) =>
            prev.some((n) => n.id === notification.id)
              ? prev
              : [notification, ...prev]
          );

          if (!notification.isSilent) {
            setShowNotification(true);
          }
          handleNotificationPayload(notification);
        });

        novu.on('notifications.unread_count_changed', () => {
          refreshUnreadCount(novu).catch((error) =>
            console.error('Failed to refresh the unread count:', error)
          );
        });

        await loadInbox(novu);
        await refreshUnreadCount(novu);

        if (!isEffectActive) {
          discardCurrentInstance();
          return;
        }

        setNovuInstance(novu);
        setIsInitialized(true);
        retryAttemptsRef.current = 0;
      } catch (error) {
        console.error('Failed to initialise the Novu SDK:', error);

        discardCurrentInstance();
        setNovuInstance(null);
        setIsInitialized(false);

        if (!isEffectActive) return;

        // Novu coming back must not need a page refresh, so this backs off towards a ceiling
        // and keeps trying rather than giving up after a fixed number of attempts.
        const delay = retryDelayForAttempt(retryAttemptsRef.current);
        retryAttemptsRef.current += 1;
        console.log(`Novu unavailable, retrying in ${Math.round(delay)}ms`);
        retryTimeoutRef.current = setTimeout(connectToNovu, delay);
      }
    };

    const reconnectWithoutWaiting = () => {
      if (!isEffectActive || connectedInstanceRef.current) return;

      retryAttemptsRef.current = 0;
      clearPendingRetry();
      connectToNovu();
    };

    const handleBrowserBackOnline = () => reconnectWithoutWaiting();

    const handleTabBecameVisible = () => {
      if (document.visibilityState === 'visible') reconnectWithoutWaiting();
    };

    window.addEventListener('online', handleBrowserBackOnline);
    document.addEventListener('visibilitychange', handleTabBecameVisible);

    connectToNovu();

    return () => {
      isEffectActive = false;
      window.removeEventListener('online', handleBrowserBackOnline);
      document.removeEventListener('visibilitychange', handleTabBecameVisible);

      clearPendingRetry();
      discardCurrentInstance();

      setNovuInstance(null);
      setIsInitialized(false);
      retryAttemptsRef.current = 0;
    };
  }, [
    enabled,
    applicationIdentifier,
    subscriberId,
    subscriberHash,
    backendUrl,
    socketUrl,
    setNotifications,
    setShowNotification,
    mapNovuNotificationToInternal,
  ]);

  const markAsRead = useCallback(
    async (notificationId: string) => {
      if (!novuInstance) return;

      try {
        const notification = notifications.find((n) => n.id === notificationId);
        if (!notification) return;

        unwrapNovuResult(
          await novuInstance.notifications.read({ notificationId })
        );

        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notificationId
              ? { ...n, readAt: new Date().toISOString() }
              : n
          )
        );
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
      }
    },
    [novuInstance, notifications, setNotifications]
  );

  const markAllAsRead = useCallback(async () => {
    if (!novuInstance) return;

    try {
      unwrapNovuResult(await novuInstance.notifications.readAll());

      const now = new Date().toISOString();
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, readAt: n.readAt || now }))
      );
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  }, [novuInstance, setNotifications]);

  const archiveNotification = useCallback(
    async (notificationId: string) => {
      if (!novuInstance) return;

      try {
        unwrapNovuResult(
          await novuInstance.notifications.archive({ notificationId })
        );

        setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      } catch (error) {
        console.error('Failed to archive notification:', error);
      }
    },
    [novuInstance, setNotifications]
  );

  const getUnreadCount = useCallback(() => {
    return notifications.filter((n) => !n.readAt).length;
  }, [notifications]);

  const refetch = useCallback(
    async (options?: { includeArchived?: boolean; includeRead?: boolean }) => {
      if (!novuInstance) return;

      try {
        const listOptions: any = {
          limit: 50,
          offset: 0,
        };

        if (!options?.includeArchived) {
          listOptions.archived = false;
        }

        if (options?.includeRead === false) {
          listOptions.read = false;
        }

        const response = await novuInstance.notifications.list(listOptions);
        const listResponse = unwrapNovuResult(response) as any;

        if (listResponse) {
          const notificationsArray = listResponse.notifications || [];
          const mappedNotifications = notificationsArray.map(
            mapNovuNotificationToInternal
          );
          setNotifications(mappedNotifications);
        }
      } catch (error) {
        console.error('Failed to refetch notifications:', error);
      }
    },
    [novuInstance, setNotifications, mapNovuNotificationToInternal]
  );

  const unarchiveNotification = useCallback(
    async (notificationId: string) => {
      if (!novuInstance) return;

      try {
        unwrapNovuResult(
          await novuInstance.notifications.unarchive({ notificationId })
        );

        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notificationId ? { ...n, archivedAt: null } : n
          )
        );
      } catch (error) {
        console.error('Failed to unarchive notification:', error);
      }
    },
    [novuInstance, setNotifications]
  );

  const deleteNotification = useCallback(
    async (notificationId: string) => {
      if (!novuInstance) return;

      try {
        unwrapNovuResult(
          await novuInstance.notifications.archive({ notificationId })
        );

        setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      } catch (error) {
        console.error('Failed to delete notification:', error);
      }
    },
    [novuInstance, setNotifications]
  );

  const snoozeNotification = useCallback(
    async (notificationId: string, until: Date) => {
      if (!novuInstance) return;

      try {
        const snoozeUntil = until.toISOString();

        const inboxActions = novuInstance.notifications as any;
        const snoozeResult = await inboxActions.snooze?.({
          notificationId,
          snoozeUntil,
        });

        if (!snoozeResult || snoozeResult.error) {
          unwrapNovuResult(
            await novuInstance.notifications.read({ notificationId })
          );
        }

        setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      } catch (error) {
        console.error('Failed to snooze notification:', error);
      }
    },
    [novuInstance, setNotifications]
  );

  function axiosRequestWithAuth(
    method: 'get' | 'post',
    url: string,
    payload?: object
  ) {
    const accessToken = localStorage.getItem('accessToken');
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    };

    if (method === 'get') {
      return axios.get(`/api${url}`, { ...config, params: payload });
    }
    if (method === 'post') {
      return axios.post(`/api${url}`, payload, config);
    }
    throw new Error('Unsupported method');
  }

  function updateWarehouseMatch(notification: NotificationType) {
    const entityId = notification.relatedEntities?.find(
      (e) => e.type === 'WarehouseMatch'
    )?.id;
    console.log('WarehouseMatch ID: ', entityId);
    if (!entityId) return;

    axiosRequestWithAuth('post', `${MATCH_LIST}`, {
      matchId: entityId,
    })
      .then((response) => {
        if (response !== null) {
          const refreshedMatch = response.data.matches[0].match;
          setMatch((matchOnScreen) =>
            refreshOnlyTheMatchOnScreen(matchOnScreen, refreshedMatch)
          );
        }
      })
      .catch((error) => {
        console.error('Error posting Supply chain process:', error);
      });
  }

  function updateWarehouse(notification: NotificationType) {
    const entityId = notification.relatedEntities?.find(
      (e) => e.type === 'Warehouse'
    )?.id;
    console.log('Warehouse ID: ', entityId);
    if (!entityId) return;
  }

  function updateInventorySku(notification: NotificationType) {
    const entityId = notification.relatedEntities?.find(
      (e) => e.type === 'InventorySku'
    )?.id;
    console.log('InventorySku ID: ', entityId);
    if (!entityId) return;

    setStockRefresh((prev) => prev + 1);
  }

  function updateSupplyChainProcess(notification: NotificationType) {
    const entityId = notification.relatedEntities?.find(
      (e) => e.type === 'SupplyChainProcess'
    )?.id;
    if (!entityId) return;

    axiosRequestWithAuth('post', `${LIST_SUPPLY_CHAIN_PROCESSES_URL}`, {
      supplyChainProcessId: entityId,
    })
      .then((response) => {
        if (response.data.supplyChainProcesses[0].type === 'Inbound') {
          setInboundProcesses((prevInboundProcesses) => {
            const index = prevInboundProcesses.findIndex(
              (process) =>
                process.id === response.data.supplyChainProcesses[0].id
            );
            if (index !== -1) {
              const updated = [...prevInboundProcesses];
              updated[index] = {
                ...updated[index],
                ...response.data.supplyChainProcesses[0],
              };
              return updated;
            }
            return [
              response.data.supplyChainProcesses[0],
              ...prevInboundProcesses,
            ];
          });
        }
        if (response.data.supplyChainProcesses[0].type === 'Outbound') {
          setOutboundProcesses((prevOutboundProcesses) => {
            const index = prevOutboundProcesses.findIndex(
              (process) =>
                process.id === response.data.supplyChainProcesses[0].id
            );
            if (index !== -1) {
              const updated = [...prevOutboundProcesses];
              updated[index] = {
                ...updated[index],
                ...response.data.supplyChainProcesses[0],
              };
              return updated;
            }
            return [
              response.data.supplyChainProcesses[0],
              ...prevOutboundProcesses,
            ];
          });
        }
      })
      .catch((error) => {
        console.error('Error posting Supply chain process:', error);
      });
  }

  function updateBillingCycle(notification: NotificationType) {
    const entityId = notification.relatedEntities?.find(
      (e) => e.type === 'BillingCycle'
    )?.id;
    console.log('BillingCycle ID: ', entityId);
    if (!entityId) return;
  }

  function updateStarterChecklist(notification: NotificationType) {
    const entityId = notification.relatedEntities?.find(
      (e) => e.type === 'StarterChecklist'
    )?.id;
    console.log('StarterChecklist ID: ', entityId);
    if (!entityId) return;
  }

  function updateVehicle(notification: NotificationType) {
    const entityId = notification.relatedEntities?.find(
      (e) => e.type === 'Vehicle'
    )?.id;
    console.log('Vehicle ID: ', entityId);
    if (!entityId) return;
  }

  function handleNotificationPayload(notification: NotificationType) {
    switch (!!notification.notificationType) {
      case notification.notificationType.startsWith('WarehouseMatch.'):
        updateWarehouseMatch(notification);
        break;
      case notification.notificationType.startsWith('Warehouse.'):
        updateWarehouse(notification);
        break;
      case notification.notificationType.startsWith('InventorySku.'):
        updateInventorySku(notification);
        break;
      case notification.notificationType.startsWith('SupplyChainProcess.'):
        updateSupplyChainProcess(notification);
        break;
      case notification.notificationType.startsWith('SalesOrder.'):
        updateSupplyChainProcess(notification);
        break;
      case notification.notificationType.startsWith('BillingCycle.'):
        updateBillingCycle(notification);
        break;
      case notification.notificationType.startsWith('StarterChecklist.'):
        updateStarterChecklist(notification);
        break;
      case notification.notificationType.startsWith('Vehicle.'):
        updateVehicle(notification);
        break;
      default:
        break;
    }
  }

  return {
    notifications,
    isInitialized,
    markAsRead,
    markAllAsRead,
    archiveNotification,
    unarchiveNotification,
    deleteNotification,
    snoozeNotification,
    getUnreadCount,
    refetch,
  };
};
